import React from 'react';
import {
  Box,
  Flex,
  Text,
  Tooltip,
  useMultiStyleConfig,
} from '@chakra-ui/react';
//import { StatisticItem } from 'queries/campaigns/fetchSummary';
import { CAMPAIGN_STATUS_COLORS } from 'components/Campaign';
import { useTranslation } from 'react-i18next';

type StatisticItem = {
  id: string;
  count: number;
  name?: string;
  color?: { bg: string; text: string };
};

const StatusSegment = ({
  elem,
  percent,
  index,
  styles,
}: {
  elem: StatisticItem;
  percent: number;
  index: number;
  styles: Record<string, any>;
}): JSX.Element => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const label = width > 80 ? elem.name : elem.count;

  return (
    <Tooltip
      label={`${elem.name}: ${elem.count} (${percent}%)`}
      placement='top'
      hasArrow
    >
      <Flex
        ref={ref}
        sx={styles.segment}
        flex={percent}
        bg={elem?.color?.bg}
        pl={index === 2 ? 4 : 2}
        ml={index > 0 ? '-16px' : 0}
        zIndex={index === 1 ? 1 : 0}
        _hover={{ filter: 'brightness(0.92)' }}
      >
        {label !== null && (
          <Text fontSize='xs' color={elem?.color?.text} noOfLines={1}>
            {percent}%
          </Text>
        )}
      </Flex>
    </Tooltip>
  );
};

export const CampaignStatusBar = ({
  data,
  ...props
}: {
  data: StatisticItem[];
  [key: string]: any;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const styles = useMultiStyleConfig('StatusBar', props);

  const statusBarGroupsDataFilteredWithColor = data
    .filter(group => group.id !== 'SETTLED')
    .map(group => {
      return {
        ...group,
        color: CAMPAIGN_STATUS_COLORS[group.id] || {
          bg: 'gray.300',
          text: 'graphite.500',
        },
      };
    });

  const total = statusBarGroupsDataFilteredWithColor.reduce(
    (sum, group) => sum + group.count,
    0,
  );

  if (total === 0) {
    return (
      <Text>
        {t('campaign.noDataAboutCampaigns', 'Brak danych o kampaniach')}
      </Text>
    );
  }

  const statusBarGroupsWithCampaigns = statusBarGroupsDataFilteredWithColor.filter(
    group => group.count > 0,
  );

  return (
    <Box w='100%' my='auto'>
      <Flex sx={styles.container}>
        {statusBarGroupsWithCampaigns.map((elem, index) => {
          const percent = Math.round((elem.count / total) * 100);
          return (
            <StatusSegment
              key={elem.id}
              elem={elem}
              percent={percent}
              index={index}
              styles={styles}
            />
          );
        })}
      </Flex>
      <Flex sx={styles.legend}>
        {statusBarGroupsWithCampaigns.map(segment => (
          <Flex key={segment.id} sx={styles.legendItem}>
            <Box __css={styles.legendDot} bg={segment?.color?.bg} />
            <Text sx={styles.label}>{segment?.name}</Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};
